import React, { useState, useEffect, useContext } from 'react'
import { Button } from 'react-bootstrap'

import globalNotification from 'live/services/global-notification'
import i18n from 'live/services/i18n'

import { defaultParamsForResourceType } from 'plugin-aeapps-resources-manager/utils/constants'

import { FilteredSearch, RemoveConfirmationModal, Scrollbars } from '../../../generic'
import { CustomList } from '../../../generic/CustomList/CustomList'
import { useDimensions } from '../../../hooks'
import { IQueryParamsAssetTypeList } from '../../../types/IQueryParamsList'
import { hasAdminPermission } from '../../../utils/permissions'
import { getAssetTypes } from '../../../utils/services'
import {
    AssetType,
    createAssetType,
    deleteAssetType,
    getAssetTypesToResourceGroupAsList,
    updateAssetType
} from '../../../utils/services/assetType'
import { ResourceTypeConfigurationRouteContext } from '../../AssetTypeConfigurationWrapper'
import { CreateEditAssetTypeModal } from './components'
import { AssetTypeListContainer } from './styles'

export const AssetTypeList: React.FC = () => {
    const { params, setParams } = useContext(ResourceTypeConfigurationRouteContext)
    const dimensions = useDimensions()

    const [loading, setLoading] = useState<boolean>(false)
    const [assetTypes, setAssetTypes] = useState<AssetType[]>()
    const [tempAssetType, setTempAssetType] = useState<AssetType>()
    const [searchTerm, setSearchTerm] = useState<string>('')
    const [showCreateModal, setShowCreateModal] = useState<boolean>(false)
    const [showEditModal, setShowEditModal] = useState<boolean>(false)
    const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false)
    const [shouldUpdate, setShouldUpdate] = useState<boolean>(true)
    const [totalData, setTotalData] = useState<number>(0)

    const handleGetAssetTypes = async (queryParams: IQueryParamsAssetTypeList): Promise<void> => {
        try {
            setLoading(true)
            if (hasAdminPermission) {
                const data = await getAssetTypesToResourceGroupAsList(queryParams)
                setAssetTypes(data.data)
                setTotalData(data.total)
            } else {
                const data = await getAssetTypes(queryParams)
                setAssetTypes(data)
            }
        } catch (error) {
            globalNotification.error(error.message ?? i18n('Unable to fetch resource types.'))
        } finally {
            setLoading(false)
        }
    }

    const handleCreateNewAssetType = async (assetTypeName: string): Promise<void> => {
        try {
            setLoading(true)
            const newOperation: AssetType = { id: assetTypeName.replace(' ', '').substring(0, 8), name: assetTypeName }
            await createAssetType(newOperation)
            handleGetAssetTypes({
                direction: params.direction,
                keyword: params.keyword,
                orderBy: params.orderBy,
                page: params.page,
                perPage: params.perPage
            })
            globalNotification.success(i18n('Resource type created successfully.'))
            // handleGetAssetTypes() TODO : evaluate why need to call get on create.
        } catch (error) {
            globalNotification.error(i18n('Unable to create resource type.'))
            setLoading(false)
        }
    }

    const handleEditAssetType = async (assetType: AssetType): Promise<void> => {
        try {
            setLoading(true)
            await updateAssetType(assetType)
            handleGetAssetTypes({
                direction: params.direction,
                keyword: params.keyword,
                orderBy: params.orderBy,
                page: params.page,
                perPage: params.perPage
            })
            globalNotification.success('Operation updated successfully.')
            // handleGetAssetTypes() TODO : evaluate why need to call get on create.
        } catch (error) {
            globalNotification.error(error.message ?? 'Unable to update operation.')
            setLoading(false)
        }
    }

    const generateTempOperationArray = (): AssetType[] => {
        return assetTypes.filter(assetType => assetType.name.toLowerCase().includes(searchTerm))
    }

    const handleShowCreateModal = (): void => {
        setShowCreateModal(true)
    }

    const handleShowEditModal = (assetType: AssetType): void => {
        setShowEditModal(true)
        setTempAssetType(assetType)
    }

    const handleShowRemoveModal = (assetType: AssetType): void => {
        setShowDeleteModal(true)
        setTempAssetType(assetType)
    }

    const handleCloseProtocol = (): void => {
        setShowCreateModal(false)
        setShowEditModal(false)
        setShowDeleteModal(false)
        setTempAssetType(null)
        setShouldUpdate(true)
    }

    const handleSortBy = (orderBy: string, direction: 'ASC' | 'DESC'): void => {
        setParams({ ...params, orderBy, direction })
        setShouldUpdate(true)
    }

    const handleDelete = async (): Promise<void> => {
        await deleteAssetType(tempAssetType.id)
        setParams(defaultParamsForResourceType)
    }
    const handlePerPage = (): void => {
        setParams({ ...params, perPage: params?.perPage ? params.perPage + 1 : 1 })
    }

    const handleSearch = (keyword: string): void => {
        setParams({ ...params, keyword })
    }

    useEffect(() => {
        // if (shouldUpdate) {
        handleGetAssetTypes({
            direction: params.direction,
            keyword: params.keyword,
            orderBy: params.orderBy,
            page: params.page,
            perPage: params.perPage
        })
        // setShouldUpdate(false)
        // }
    }, [params])

    return (
        <Scrollbars height={dimensions.height}>
            <AssetTypeListContainer className="container">
                <div className="first-row-container">
                    <Button
                        className="no-outline-button"
                        bsStyle="primary"
                        onClick={handleShowCreateModal}
                        style={{ borderRadius: 3, marginRight: 8 }}
                        id="diagramList-list-button-newDiagram"
                    >
                        <i className="far fa-plus custom-plus" /> {i18n('New Resource Type')}
                    </Button>

                    <FilteredSearch
                        keyword={params.keyword}
                        inputId="diagramList-list-field-searchDiagram"
                        onSearch={handleSearch}
                    />
                </div>
                {loading ? (
                    <div className="loading-spin">
                        <i className="fa fa-spinner fa-spin fa-x2" />
                    </div>
                ) : assetTypes?.length ? (
                    <CustomList
                        label={i18n('Resource Types')}
                        list={assetTypes}
                        columns={[
                            { title: 'ID', sortable: true, listRef: 'id' },
                            { title: 'NAME', sortable: true, listRef: 'name' }
                        ]}
                        sortDirection={params.direction}
                        sortField={params.orderBy}
                        tableProps={{ style: { width: '100%' } }}
                        configOptions={[
                            { icon: 'far fa-edit', onClickConfig: o => handleShowEditModal(o) },

                            { icon: 'far fa-trash-alt', onClickConfig: o => handleShowRemoveModal(o) }
                        ]}
                        pagination={{
                            type: 'live',
                            totalItems: totalData ?? assetTypes.length,
                            showingItems: assetTypes.length,
                            onPerPageChange: handlePerPage,
                            perPage: params.perPage
                        }}
                        onSortClick={handleSortBy}
                    />
                ) : (
                    <div className="no-asset-type">
                        <i className="far fa-folder-open " />
                        <p>{i18n('There are no resource types yet')}</p>
                        <a onClick={handleShowCreateModal}>{i18n('Create new resource type')}</a>
                    </div>
                )}
                <CreateEditAssetTypeModal
                    show={showCreateModal}
                    setShow={setShowCreateModal}
                    type={'create'}
                    handleClose={handleCloseProtocol}
                    handleCreate={handleCreateNewAssetType}
                />
                <CreateEditAssetTypeModal
                    show={showEditModal}
                    setShow={setShowEditModal}
                    type={'edit'}
                    handleClose={handleCloseProtocol}
                    assetType={tempAssetType}
                    setOperation={setTempAssetType}
                    handleUpdate={handleEditAssetType}
                />
                <RemoveConfirmationModal
                    setShow={setShowDeleteModal}
                    show={showDeleteModal}
                    handleClose={handleCloseProtocol}
                    type={'resource type'}
                    message={i18n('If you really want to delete the resource type, type')}
                    confirmationMessage={i18n('Resource type deleted succesfully.')}
                    removalMessage={`${i18n('Are you sure you want remove the resource type')} ${tempAssetType?.name}?`}
                    onDelete={handleDelete}
                />
            </AssetTypeListContainer>
        </Scrollbars>
    )
}
